import { CartItem } from 'models/CartItem';
import { AppearanceTypes } from 'components/Shared/Button';
import Image from 'components/Shared/Image';
import styles from './styles.scss';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'components/Shared/responsive';
import { isMultiQuantity } from 'components/Basket/selector';
import { StockStatus } from 'models/ProductMeta';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { IconPlus } from 'components/Shared/Icon/themes/BGH-RD3/iconPlus';
import { IconMinus } from 'components/Shared/Icon/themes/BGH-RD3/iconMinus';
import { IconCross } from 'components/Shared/Icon/themes/BGH-RD3/iconCross';
import { Button } from 'components/Button';
import { Input } from 'components/Shared/Form/themes/BGH-RD3/input';
import { cartProductRemove, updateQuantity, updateRentalPeriod } from 'components/Cart/actions';
import cn from 'classnames';
import { useDebounce, useToggle } from 'hooks';
import Spinner from 'components/Shared/Loader';
import { hasElements, setUnitLabel, NO_ELEMENT } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { handleServerError } from 'components/Shared/server/request';
import { getIsRentalProduct } from 'components/Shared/Price/selector';
import { RentalPeriod } from './rental/rentalPeriod';
import { rm } from 'utils';
import { AdminCheckoutProductRow } from 'authorization';
import { selectIsValidated } from 'store/appShell/selector';
import { RelationshipType } from 'models/RelationshipType';
import { GTM_LIST_NAME } from 'store/googleTagManager/types';
import { selectShouldUseNewCart } from '../DeliveryInformation/selector';
import { PriceContainerBob } from 'views/CheckoutPage/components/PriceContainer/priceContainerBob';
import { PriceContainer } from 'views/CheckoutPage/components/PriceContainer/PriceContainer';

interface OwnProps {
  appearance?: AppearanceTypes;
  className?: string;
  removed?: boolean;
}

interface MergedProps extends OwnProps {
  item: CartItem;
  relations: CartItem[];
  shouldBeDisabled: boolean;
  isSubItem?: boolean;
  isCharge?: boolean;
  setShouldBeDisabled: (value: boolean) => void;
}

export interface RelationSubItem extends CartItem {
  itemType: RelationshipType;
}

const imageStyleDesktop = {
  width: 120,
  height: 120
};

const imageStyleMobile = {
  width: 74,
  height: 74
};

const isProductUrl = (url?: string) => url !== undefined && !url.startsWith('///');

interface ItemHeaderProps {
  item: CartItem;
}

const ItemHeader = ({ item }: ItemHeaderProps) => (
  <>
    <div className={styles.displayName}>{item.displayName}</div>
    {item.variantName && <div className={styles.variantName}>{item.variantName}</div>}
  </>
);

interface StatusProps {
  isVisible: boolean;
  text: string;
}

const Status = ({ isVisible, text }: StatusProps): JSX.Element | null => {
  if (!isVisible) return null;
  return (
    <div>
      <div className={styles.manufactureDot} />
      <div className={styles.stockStatus}>{text}</div>
    </div>
  );
};

const DEBOUNCE_TIME = 500;

export const CheckoutProduct = ({
  item,
  relations,
  shouldBeDisabled,
  isSubItem,
  isCharge,
  setShouldBeDisabled
}: MergedProps) => {
  const defaultPickupDate = item?.rentalPeriod?.pickupDate
    ? new Date(item.rentalPeriod.pickupDate)
    : undefined;
  const defaultReturnDate = item?.rentalPeriod?.returnDate
    ? new Date(item.rentalPeriod.returnDate)
    : undefined;

  const isRentalProduct = getIsRentalProduct(item);
  const dispatch = useDispatch();
  const mobile = useSelector(isMobile);
  const imageStyle = mobile ? imageStyleMobile : imageStyleDesktop;
  const [isLoading, setLoading] = useToggle(false);
  const [quantityAmount, setQuantityAmount] = useState(item.quantity.amount.toString());
  const [selectedPickupDate, setPickupDate] = useState<Date | undefined>(defaultPickupDate);
  const [selectedReturnDate, setReturnDate] = useState<Date | undefined>(defaultReturnDate);
  const isValidated = useSelector(selectIsValidated);
  const shouldUseNewCart = useSelector(selectShouldUseNewCart);

  // ONLY FOR ADMIN: To visually update the subitem
  // quantity if the parent changes quantity
  useEffect(() => {
    if (isSubItem) {
      setQuantityAmount(item.quantity.amount.toString());
    }
  }, [isSubItem, item.quantity.amount]);

  const toggleRequestIsLoading = useCallback(
    (requestStatus: boolean) => {
      setLoading(requestStatus);
      setShouldBeDisabled(requestStatus);
    },
    [setLoading, setShouldBeDisabled]
  );

  const [dispatchRentalPeriod] = useDebounce(() => {
    if (!selectedPickupDate || !selectedReturnDate) return;

    toggleRequestIsLoading(true);
    dispatch(
      updateRentalPeriod({
        cartItemId: item.cartItemId,
        rentalPeriod: { pickupDate: selectedPickupDate, returnDate: selectedReturnDate }
      })
    ).catch(res => {
      toggleRequestIsLoading(false);
      handleServerError(dispatch)(res);
    });
  }, DEBOUNCE_TIME);

  const [dispatchNewAmount] = useDebounce((item: CartItem, newQuantity: number) => {
    const oldQuantity = item.quantity.amount;
    toggleRequestIsLoading(true);
    dispatch(
      updateQuantity({
        cartItemId: item.cartItemId,
        parentId: item.adminOverrideRequest?.cartItemRelationId,
        quantity: newQuantity,
        gtmListName: GTM_LIST_NAME.CHECKOUT
      })
    ).catch(res => {
      toggleRequestIsLoading(false);
      setQuantityAmount(oldQuantity.toString());
      handleServerError(dispatch)(res);
    });
  }, DEBOUNCE_TIME);

  useEffect(() => {
    toggleRequestIsLoading(false);
  }, [item.linePrice, toggleRequestIsLoading]);

  const removeProduct = useCallback(
    (item: CartItem) => {
      toggleRequestIsLoading(true);
      void dispatch(
        cartProductRemove({
          cartItemId: item.cartItemId,
          productId: item.productId,
          gtmListName: GTM_LIST_NAME.CHECKOUT
        })
      );
    },
    [dispatch, toggleRequestIsLoading]
  );

  const quantityChange = useCallback(
    (item: CartItem, newQuantity: number) => {
      setQuantityAmount(newQuantity.toString());
      dispatchNewAmount(item, newQuantity);
    },
    [setQuantityAmount, dispatchNewAmount]
  );

  const manualQuantity = useCallback(
    (e: ChangeEvent<HTMLInputElement>, item: CartItem) => {
      const newAmount = Number(e.currentTarget.value);

      if (newAmount === item.quantity.amount) {
        return;
      }

      quantityChange(item, newAmount);
    },
    [quantityChange]
  );

  return (
    <>
      <div
        className={cn(styles.productContainer, {
          [styles.productBorder]: !isValidated,
          [styles.adminMultiQuantityProduct]:
            shouldUseNewCart && isMultiQuantity(item.quantity) && isValidated
        })}
        key={item.cartItemId}
        {...getQaDataAttr(`checkout-product-${item.cartItemId}`)}
      >
        {isLoading && <Spinner className={styles.spinner} loading />}
        <div
          className={cn(styles.thumbnailContainer, {
            [styles.loading]: isLoading
          })}
        >
          {item.image.id ? (
            <a href={item.productUrl} style={imageStyle}>
              <Image
                {...{
                  ...imageStyle,
                  ...item.image,
                  className: styles.image,
                  alt: item.displayName
                }}
              />
            </a>
          ) : (
            <div className={styles.image} style={imageStyle} />
          )}
        </div>
        <div
          className={cn(styles.nameContainer, {
            [styles.loading]: isLoading
          })}
        >
          <div className={styles.header}>
            {isProductUrl(item.productUrl) ? (
              <a href={item.productUrl} className={styles.link}>
                <ItemHeader item={item} />
              </a>
            ) : (
              <span className={styles.link}>
                <ItemHeader item={item} />
              </span>
            )}
            <Status isVisible={isRentalProduct} text={rm('rental_product')} />
            <Status
              isVisible={item.meta.stockStatus === StockStatus.Create && !isRentalProduct}
              text={item.meta.stock}
            />
          </div>
        </div>
        {hasElements(relations) ? (
          <div
            className={cn(styles.addons, {
              [styles.loading]: isLoading
            })}
          >
            <ul className={styles.subItems}>
              {relations
                .filter(subItem => !subItem.isBundleProduct)
                .map(subItem => (
                  <li className={styles.subItem} key={subItem.cartItemId}>
                    <span>+</span>
                    <span>
                      {subItem.quantity.amount > 1 && (
                        <span>
                          {subItem.quantity.amount} {subItem.quantity.unit}
                        </span>
                      )}
                      {subItem.displayName}
                    </span>
                  </li>
                ))}
            </ul>
            {isRentalProduct && (
              <RentalPeriod
                setPickupDate={setPickupDate}
                selectedPickupDate={selectedPickupDate}
                setReturnDate={setReturnDate}
                selectedReturnDate={selectedReturnDate}
                shouldBeDisabled={shouldBeDisabled}
                dispatchRentalPeriod={dispatchRentalPeriod}
                item={item}
              />
            )}
          </div>
        ) : (
          <>
            {isRentalProduct ? (
              <div
                className={cn(styles.addons, {
                  [styles.loading]: isLoading
                })}
              >
                <RentalPeriod
                  setPickupDate={setPickupDate}
                  selectedPickupDate={selectedPickupDate}
                  setReturnDate={setReturnDate}
                  selectedReturnDate={selectedReturnDate}
                  shouldBeDisabled={shouldBeDisabled}
                  dispatchRentalPeriod={dispatchRentalPeriod}
                  item={item}
                />
              </div>
            ) : (
              NO_ELEMENT
            )}
          </>
        )}
        {!isSubItem && !isCharge && (
          <div
            className={cn(styles.delete, {
              [styles.loading]: isLoading
            })}
          >
            <Button
              appearance={'link'}
              onClick={() => {
                removeProduct(item);
              }}
              {...getQaDataAttr('delete-item-from-cart')}
            >
              <IconCross />
            </Button>
          </div>
        )}

        <div
          className={cn(styles.quantityContainer, {
            [styles.loading]: isLoading
          })}
        >
          <div className={styles.quantitySelector}>
            {shouldUseNewCart && isMultiQuantity(item.quantity) && isValidated && (
              <div className={styles.adminMultiQuantitySelector}>
                <Input
                  type="number"
                  disabled={shouldBeDisabled}
                  value={quantityAmount}
                  unit={item.quantity.salesUnit}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    e.preventDefault();
                    setQuantityAmount(e.target.value);
                  }}
                  onFocus={(e: ChangeEvent<HTMLInputElement>) => {
                    e.currentTarget.select();
                  }}
                  onKeyUp={e => {
                    e.preventDefault();

                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                    manualQuantity(e, item);
                  }}
                  inputMode="numeric"
                  className={styles.quantityInput}
                />
              </div>
            )}
            <Input
              type="number"
              disabled={(shouldUseNewCart && isMultiQuantity(item.quantity)) || shouldBeDisabled}
              value={
                isMultiQuantity(item.quantity) && shouldUseNewCart
                  ? item.quantity.salesQuantity
                  : quantityAmount
              }
              unit={
                isMultiQuantity(item.quantity) && shouldUseNewCart
                  ? item.quantity.unit
                  : setUnitLabel(item.quantity.unit, mobile)
              }
              className={styles.quantityInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();
                setQuantityAmount(e.target.value);
              }}
              onKeyUp={e => {
                e.preventDefault();

                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                manualQuantity(e, item);
              }}
              onFocus={(e: ChangeEvent<HTMLInputElement>) => {
                e.currentTarget.select();
              }}
              inputMode="numeric"
              {...getQaDataAttr('item-quantity-input')}
            />
            <Button
              className={styles.quantityButton}
              appearance="tertiary"
              onClick={() => {
                quantityChange(item, Number(quantityAmount) - 1);
              }}
              disabled={item.quantity.lowest === Number(quantityAmount) || shouldBeDisabled}
              {...getQaDataAttr('checkout-quantity-decrease')}
            >
              <IconMinus />
            </Button>
            <Button
              className={styles.quantityButton}
              appearance="tertiary"
              onClick={() => {
                quantityChange(item, Number(quantityAmount) + 1);
              }}
              disabled={item.quantity.highest === Number(quantityAmount) || shouldBeDisabled}
              {...getQaDataAttr('checkout-quantity-increase')}
            >
              <IconPlus />
            </Button>
          </div>
          {isMultiQuantity(item.quantity) && !shouldUseNewCart && (
            <div className={styles.multiQuantity}>
              x&nbsp;{item.quantity.factor}&nbsp;=&nbsp;{item.quantity.salesQuantity}&nbsp;
              {item.quantity.unit}
            </div>
          )}
        </div>
        {shouldUseNewCart ? (
          <PriceContainerBob item={item} isLoading={isLoading} />
        ) : (
          <PriceContainer item={item} isLoading={isLoading} relations={relations} />
        )}
      </div>
      <AdminCheckoutProductRow cartItem={item} relations={relations as RelationSubItem[]} />
    </>
  );
};
